import {useCallback, useEffect, useRef, useSyncExternalStore} from 'react'
import {localStorageStore} from './local-storage-store'
import type {Listener} from './create-store'
import type {WriteOptions} from './types'
import {validOrDefault, type Validator} from './validation'

const {
  cache,
  readStorage,
  writeStorage,
  resetStorage,
  subscribe,
  clearKeys,
  clearAll: clearAllLocalStorage,
} = localStorageStore

/**
 * Clears all store-tracked localStorage keys, their in-memory cache,
 * and the underlying browser localStorage.
 */
export function clearAllLocalStorageCache(): void {
  clearAllLocalStorage()
  if (typeof window !== 'undefined') {
    window.localStorage.clear()
  }
}

/**
 * Clears specified keys from localStorage and notifies subscribers
 */
export function clearLocalStorage(keys: string[]): void {
  clearKeys(keys)
}

interface LocalStorageOptions<T> {
  /**
   * Type validation function or TypeBox-style validator object to validate read values. If an
   * encountered value fails validation, it will be silently discarded. For complex types, a TypeBox
   * validator can be used:
   * ```ts
   * const schema = Type.Object(...)
   * const validator = Compile(schema)
   * //...
   * useLocalStorage(key, initialValue, {validator})
   * ```
   */
  validator: Validator<T>
}

type UseLocalStorageResult<T> = [
  value: T,
  setValue: (value: T | ((prev: T) => T), options?: WriteOptions) => void,
  reset: () => void,
]

/** @deprecated Omitting `options.validator` is unsafe. Provide a `validator` in an `options` object as the third argument. */
export function useLocalStorage<T>(key: string, initialValue: T): UseLocalStorageResult<T>
/**
 * React hook for localStorage with cross-component synchronization.
 *
 * @param key Unique key for this entry.
 * **Caution**: storage is shared across the entire app. If the same key is used in multiple places, take care to
 * ensure both callsites use matching data types. If the types conflict, they will clash with each other and result in
 * discarded data due to failed validation checks.
 * @param initialValue Initial / default value.
 * @param options Additional options including validator.
 * @returns `[value, setValue, reset]` tuple.
 */
export function useLocalStorage<T>(
  key: string,
  initialValue: T,
  options: LocalStorageOptions<T>,
): UseLocalStorageResult<T>
export function useLocalStorage<T>(
  key: string,
  initialValue: T,
  options?: LocalStorageOptions<T>,
): UseLocalStorageResult<T> {
  // Use ref for initialValue so it's available in callbacks without stale closure issues
  const initialValueRef = useRef(initialValue)
  useEffect(() => {
    initialValueRef.current = initialValue
  })

  const optionsRef = useRef(options)
  useEffect(() => {
    optionsRef.current = options
  })

  // Seed lazily inside getSnapshot (idempotent) instead of during render
  // to avoid mutating external state in the render phase (concurrent-safe).
  // Uses initialValue/validator directly (not ref) because getSnapshot runs during render
  // before the effect that updates initialValueRef.
  const getSnapshot = useCallback((): T => {
    if (!cache.has(key)) {
      readStorage(key, initialValue)
    }
    return validOrDefault(cache.get(key), initialValue, options?.validator)
  }, [key, initialValue, options?.validator])

  const getServerSnapshot = useCallback((): T => {
    return initialValue
  }, [initialValue])

  const subscribeToStore = useCallback((listener: Listener) => subscribe(key, listener), [key])

  const value = useSyncExternalStore(subscribeToStore, getSnapshot, getServerSnapshot)

  const setValue = useCallback(
    (next: T | ((prev: T) => T), writeOptions?: WriteOptions) => {
      const current = cache.has(key)
        ? validOrDefault(cache.get(key), initialValueRef.current, optionsRef.current?.validator)
        : initialValueRef.current

      const resolved = typeof next === 'function' ? (next as (prev: T) => T)(current) : next
      // Treat undefined as a reset to avoid writing "undefined" string to storage
      if (resolved === undefined) {
        resetStorage(key, initialValueRef.current)
      } else {
        writeStorage(key, resolved, writeOptions)
      }
    },
    [key],
  )

  const reset = useCallback(() => {
    resetStorage(key, initialValueRef.current)
  }, [key])

  return [value, setValue, reset] as const
}
